"use client";

import { c as _c } from "react/compiler-runtime";
import EyeOffIcon from "@/assets/icons/eye-off.svg";
import EyeIcon from "@/assets/icons/eye.svg";
import { Button, Dialog, GlassButton } from "@/shared/ui";
import { cn } from "@/lib/utils";
import * as React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = "email" | "confirm";
export type ResetPasswordDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fills the email field */
  defaultEmail?: string;
  onSuccess?: () => void;
};

// ─── Info block ───────────────────────────────────────────────────────────────

function InfoBlock(t0) {
  const $ = _c(2);
  const {
    message
  } = t0;
  let t1;
  if ($[0] !== message) {
    t1 = <div className="flex items-start gap-[10px] p-3 rounded-[12px] bg-[rgba(11,214,106,0.1)] border border-[#0bd66a] overflow-hidden"><p className="text-[14px] font-normal leading-5 text-[#0bd66a]">{message}</p></div>;
    $[0] = message;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// ─── Input field ──────────────────────────────────────────────────────────────

function GlassInput(t0) {
  const $ = _c(21);
  const {
    label,
    hint,
    id,
    type,
    ...props
  } = t0;
  const isPassword = type === "password";
  const isDisabled = props.disabled || props.readOnly;
  const [showPassword, setShowPassword] = React.useState(false);
  let inputType = type;
  if (isPassword) {
    inputType = showPassword ? "text" : "password";
  }
  const t1 = "flex flex-col gap-[4px] w-full";
  let t2;
  if ($[0] !== id || $[1] !== label) {
    t2 = <label htmlFor={id} className="font-[family-name:var(--font-poppins)] text-[12px] font-normal leading-4 text-white/80">{label}</label>;
    $[0] = id;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = "relative h-[36px] w-full";
  const t4 = inputType;
  const t5 = cn("w-full h-full py-2 rounded-[12px]", isPassword ? "pl-3 pr-10" : "pl-3 pr-1.5", "bg-white/5 text-[14px] font-normal leading-5 text-white", "placeholder:text-white/60", "outline-none focus:ring-1 focus:ring-white/20", "shadow-[0px_2px_10px_0px_rgba(0,0,0,0.25),inset_0.5px_0.5px_0.5px_0px_rgba(255,255,255,0.7),inset_-0.5px_-0.5px_1px_0px_rgba(255,255,255,0.3)]", isDisabled && "opacity-50 cursor-not-allowed");
  let t6;
  if ($[3] !== id || $[4] !== inputType || $[5] !== props || $[6] !== t5) {
    t6 = <input id={id} type={t4} className={t5} {...props} />;
    $[3] = id;
    $[4] = inputType;
    $[5] = props;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isPassword || $[9] !== setShowPassword || $[10] !== showPassword) {
    t7 = isPassword && <button type="button" tabIndex={-1} onClick={() => setShowPassword(_temp)} className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center size-6 text-white/60 hover:text-white/90 transition-colors" aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOffIcon width={20} height={20} /> : <EyeIcon width={20} height={20} />}</button>;
    $[8] = isPassword;
    $[9] = setShowPassword;
    $[10] = showPassword;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className={t3}>{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== hint) {
    t9 = hint && <p className="text-[12px] font-normal leading-4 text-white/80">{hint}</p>;
    $[15] = hint;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t2 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div className={t1}>{t2}{t8}{t9}</div>;
    $[17] = t2;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}

// ─── Component ────────────────────────────────────────────────────────────────
function _temp(v) {
  return !v;
}
export function ResetPasswordDialog({
  open,
  onOpenChange,
  defaultEmail = "",
  onSuccess
}: ResetPasswordDialogProps) {
  const [step, setStep] = React.useState<Step>("email");
  const [email, setEmail] = React.useState(defaultEmail);
  const [code, setCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  // Reset form when the dialog closes or defaultEmail changes while closed
  // (adjust state during render — see https://react.dev/learn/you-might-not-need-an-effect)
  const [resetSnapshot, setResetSnapshot] = React.useState({
    open,
    defaultEmail
  });
  if (!open && (open !== resetSnapshot.open || defaultEmail !== resetSnapshot.defaultEmail)) {
    setResetSnapshot({
      open,
      defaultEmail
    });
    setEmail(defaultEmail);
    setStep("email");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSuccess(false);
  } else if (open !== resetSnapshot.open || defaultEmail !== resetSnapshot.defaultEmail) {
    setResetSnapshot({
      open,
      defaultEmail
    });
  }
  async function handleSendCode() {
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const {
        CognitoDirectAuth
      } = await import("@/lib/cognito-direct-auth");
      const result = await CognitoDirectAuth.forgotPassword(email.trim());
      if (result.success) {
        setStep("confirm");
      } else {
        setError(result.error ?? "Failed to send verification code.");
      }
    } finally {
      setIsLoading(false);
    }
  }
  async function handleConfirm() {
    if (!code.trim()) {
      setError("Please enter the verification code.");
      return;
    }
    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const {
        CognitoDirectAuth
      } = await import("@/lib/cognito-direct-auth");
      const result = await CognitoDirectAuth.confirmForgotPassword(email.trim(), code.trim(), newPassword);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error ?? "Failed to reset password.");
      }
    } finally {
      setIsLoading(false);
    }
  }
  function handleClose() {
    onOpenChange(false);
    if (success) {
      onSuccess?.();
    }
  }
  let submitLabel = "Reset Password";
  if (isLoading) {
    submitLabel = "Please wait…";
  } else if (step === "email") {
    submitLabel = "Send Code";
  }
  return <Dialog opened={open} onClose={handleClose} title={step === "email" ? "Password Reset" : "Enter Verification Code"} size={360} withCloseButton contentClassName="!bg-[#131313]" bodyClassName="!bg-[#131313]" mantineBodyClassName="!bg-[#131313]">
      <div className="flex flex-col gap-8 pt-4 pb-0">
        {success && <div className="flex flex-col items-center gap-2 py-4 text-center">
            <p className="text-[14px] font-bold text-white">
              Password reset successfully!
            </p>
            <p className="text-[12px] text-white/60">
              You can now log in with your new password.
            </p>
          </div>}
        {!success && step === "email" && <GlassInput id="reset-email" label="Your Email Address" type="email" placeholder="example@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e_0 => e_0.key === "Enter" && handleSendCode()} hint="A verification code will be sent to this address" autoComplete="email" autoFocus readOnly={!!defaultEmail} />}
        {!success && step !== "email" && <div className="flex flex-col gap-3 w-full">
            <InfoBlock message="Verification code sent to email!" />
            <GlassInput id="reset-code" label="Verification Code" type="text" placeholder="Enter 6-digit code" value={code} onChange={e_1 => setCode(e_1.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} autoComplete="one-time-code" autoFocus />
            <GlassInput id="reset-new-password" label="New Password" type="password" placeholder="Password" value={newPassword} onChange={e_2 => setNewPassword(e_2.target.value)} autoComplete="new-password" hint="Min 8 characters, uppercase, lowercase, number & special character" />
            <GlassInput id="reset-confirm-password" label="Confirm Password" type="password" placeholder="Re-enter new password" value={confirmPassword} onChange={e_3 => setConfirmPassword(e_3.target.value)} onKeyDown={e_4 => e_4.key === "Enter" && handleConfirm()} autoComplete="new-password" />
          </div>}

        {error && <p className="text-[12px] text-red-400 -mt-6">{error}</p>}

        {!success && <div className="flex gap-4 w-full items-start">
            <GlassButton size="md" className="flex-1 h-9 rounded-[12px] text-[14px]" onClick={step === "email" ? handleClose : () => {
          setStep("email");
          setError(null);
        }} disabled={isLoading}>
              {step === "email" ? "Cancel" : "Back"}
            </GlassButton>

            <Button variant="primary" size="md" className="flex-1 rounded-[12px] text-[14px] px-4 shadow-none drop-shadow-[0px_2px_5px_rgba(0,0,0,0.25)]" onClick={step === "email" ? handleSendCode : handleConfirm} disabled={isLoading}>
              {submitLabel}
            </Button>
          </div>}

        {success && <Button variant="primary" size="md" className="w-full rounded-[12px] text-[14px]" onClick={handleClose}>
            Done
          </Button>}
      </div>
    </Dialog>;
}